import { getApiErrorDetails } from '@amglobal/api-client'
import * as Sentry from '@sentry/react'
import { isAxiosError } from 'axios'
import { getPublicEnv } from '@/config/public-env'

/** Anonymous auth endpoints — user-initiated; never page Sentry on failure. */
const USER_AUTH_FLOW_PATH =
  /\/auth\/(?:login|register|refresh-token|forgot-password|verify-reset-otp|reset-password)(?:\/|\?|$)/i

export type CaptureApiErrorContext = {
  /** TanStack Query meta — skip Sentry for expected user-flow failures (e.g. sign-in). */
  skipSentry?: boolean
}

export function isUserAuthFlowApiError(error: unknown): boolean {
  if (!isAxiosError(error)) return false
  const url = error.config?.url ?? ''
  return USER_AUTH_FLOW_PATH.test(url)
}

/** Axios failures with no HTTP response — dev proxy/HMR restarts, offline clients, aborted requests. */
export function isTransientNetworkError(error: unknown): boolean {
  if (!isAxiosError(error)) return false
  if (error.response !== undefined) return false
  if (error.code === 'ERR_CANCELED') return true
  if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') return true
  return false
}

function readSentryEnv() {
  return {
    dsn: import.meta.env.VITE_SENTRY_DSN?.trim() ?? '',
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT?.trim() || import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE?.trim() || undefined,
  }
}

/** No-op when VITE_SENTRY_DSN is empty — dev works without config. */
export function initSentry(): void {
  const { dsn, environment, release } = readSentryEnv()
  if (!dsn) return

  const { apiBaseUrl } = getPublicEnv()

  Sentry.init({
    dsn,
    environment,
    release,
    sendDefaultPii: false,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
    ],
    tracePropagationTargets: [apiBaseUrl],
    tracesSampleRate: 0.2,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event) {
      const message = event.exception?.values?.[0]?.value ?? ''
      if (message === 'Network Error' || message.includes('Network Error')) {
        return null
      }
      return event
    },
  })
}

/** Expected 4xx and user auth flows → breadcrumb only. Unexpected 5xx / network → Sentry issue. */
export function captureApiError(
  error: unknown,
  context: CaptureApiErrorContext = {},
): void {
  const { dsn } = readSentryEnv()
  if (!dsn) return

  const { status, traceId, errorCode } = getApiErrorDetails(error)
  const isServerOrNetwork = status === undefined || status >= 500
  const skipForUserAuth =
    context.skipSentry === true || isUserAuthFlowApiError(error)
  const skipForTransientNetwork = isTransientNetworkError(error)

  if (!isServerOrNetwork || skipForUserAuth || skipForTransientNetwork) {
    Sentry.addBreadcrumb({
      category: 'api',
      level: 'warning',
      data: { status, traceId, errorCode, skipForUserAuth, skipForTransientNetwork },
    })
    return
  }

  Sentry.withScope((scope) => {
    if (traceId) scope.setTag('trace_id', traceId)
    if (errorCode) scope.setTag('error_code', errorCode)
    if (status) scope.setTag('http_status', String(status))
    Sentry.captureException(error)
  })
}

export function captureRenderError(
  error: unknown,
  componentStack?: string | null,
): void {
  const { dsn } = readSentryEnv()
  if (!dsn) return

  Sentry.withScope((scope) => {
    scope.setTag('boundary', 'app')
    if (componentStack) {
      scope.setContext('react', { componentStack })
    }
    Sentry.captureException(error)
  })
}
